import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Section, 
  Card, 
  Button,
  FadeInUp,
  StaggerContainer,
  StaggerItem,
} from '../../../components/common';
import { usePageTitle } from '../../../hooks/usePageTitle';
import { ONLINE_COURSES_HERO, TEACHER_PHOTOS, ONLINE_COURSES_VIDEO_PREVIEW, ONLINE_COURSES_THUMBNAILS } from '../../../constants';

// Módulos do curso
const COURSE_MODULES = [
  { 
    number: 1, 
    title: 'Greetings', 
    topics: ['Saudações em inglês', 'How are you today?', 'Primeiras conversas']
  },
  { 
    number: 2, 
    title: 'Family & Numbers', 
    topics: ['Possessive adjectives', 'Membros da família', 'Números', "Don't forget!"]
  },
  { 
    number: 3, 
    title: 'Describing People', 
    topics: ['Adjectives', 'Personalidade', 'Verb To Be']
  },
  { 
    number: 4, 
    title: 'Daily Routine', 
    topics: ['Days of the week', 'Rotina diária', 'Verbs', 'Simple Present']
  },
  { 
    number: 5, 
    title: 'Food & Drinks', 
    topics: ['Comidas e bebidas', 'Expressing like/dislike', 'At the restaurant']
  },
  { 
    number: 6, 
    title: 'Places & Directions', 
    topics: ['Places around town', 'Prepositions', 'Giving directions']
  },
  { 
    number: 7, 
    title: 'Weather & Seasons', 
    topics: ['Clima e estações', 'Weather vocabulary', 'Making small talk']
  },
  { 
    number: 8, 
    title: 'Hobbies', 
    topics: ['Hobbies e atividades', 'Present Continuous', 'What are you doing?']
  },
  { 
    number: 9, 
    title: 'Frequency', 
    topics: ['Adverbs of Frequency', 'How often...?', 'Daily habits']
  },
  { 
    number: 10, 
    title: 'Review Time', 
    topics: ['Revisão geral', 'Prática de conversação', 'Próximos passos']
  },
];

// Depoimentos em vídeo
const VIDEO_TESTIMONIALS = [
  {
    id: 'testimonial-1',
    youtubeId: 'TTQ1TeZKEgI',
    author: 'João Felipe',
    location: 'Olinda, Pernambuco',
  },
  {
    id: 'testimonial-2',
    youtubeId: 'a9SNXu6jMuU',
    author: 'Brena Medeiros',
    location: 'Recife',
  },
  {
    id: 'testimonial-3',
    youtubeId: 'IRcuBJycBeE',
    author: 'Ivoneth Gadelha',
    location: 'Igarassu, Recife',
  },
  {
    id: 'testimonial-4',
    youtubeId: 'qOcDcGNvbFg',
    author: 'Ana Paula',
    location: 'London, Canadá',
  },
];

// Locais onde pode estudar
const STUDY_LOCATIONS = [
  { icon: 'ri-plane-line', place: 'No avião' },
  { icon: 'ri-cup-line', place: 'No café' },
  { icon: 'ri-bus-line', place: 'No ônibus' },
  { icon: 'ri-home-4-line', place: 'Em casa' },
  { icon: 'ri-global-line', place: 'Viajando' },
];

export default function OnlineCourses() {
  usePageTitle('Jornada Online');
  const [activeModule, setActiveModule] = useState(0);
  const [showVideoPreview, setShowVideoPreview] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState<string | null>(null);
  const [showAllModules, setShowAllModules] = useState(false);

  return (
    <>
      {/* Hero - Imersivo com foco em liberdade */}
      <section className="relative min-h-screen bg-earth-900 overflow-hidden">
        {/* Background com parallax de imagens de viagem */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 grid grid-cols-3 gap-1 opacity-30">
            {ONLINE_COURSES_HERO.map((img) => (
              <img key={img.id} src={img.url} alt="" className="w-full h-full object-cover" />
            ))}
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-earth-900 via-earth-900/95 to-earth-900" />
        </div>

        <div className="container-custom relative z-10 pt-24 sm:pt-28 pb-16 sm:pb-20 min-h-screen flex flex-col justify-center">
          <div className="max-w-4xl mx-auto text-center px-2 sm:px-0">
            {/* Badge flutuante */}
            <motion.div 
              className="inline-flex items-center gap-2 bg-olive-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium mb-6 sm:mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <i className="ri-gift-line"></i>
              Bônus: 2 aulas ao vivo inclusas
            </motion.div>
            
            <motion.h1 
              className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              Aprenda inglês<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-olive-400 to-sand-300">
                onde você estiver
              </span>
            </motion.h1>
            
            <motion.p 
              className="text-base sm:text-xl text-sand-300 mb-6 sm:mb-8 max-w-2xl mx-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              Curso completo que cabe no seu bolso e no seu celular. 
              Estude no ônibus, no avião, na praia — em qualquer lugar do mundo.
            </motion.p>

            {/* Locais onde pode estudar */}
            <motion.div 
              className="flex flex-wrap justify-center gap-3 mb-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              {STUDY_LOCATIONS.map((loc, idx) => (
                <motion.div
                  key={loc.place}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm border border-sand-400/30 rounded-full text-sand-300 text-sm hover:bg-white/15 hover:border-sand-400/50 transition-all"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.4 + idx * 0.08, type: 'spring' }}
                  whileHover={{ y: -2 }}
                >
                  <i className={`${loc.icon} text-lg text-olive-400`}></i>
                  <span className="font-medium">{loc.place}</span>
                </motion.div>
              ))}
            </motion.div>

            {/* Preço e CTA principal */}
            <motion.div
              className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-md border border-white/20 rounded-2xl p-4 sm:p-6 md:p-8 max-w-md mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <div className="text-center mb-4 sm:mb-6">
                <span className="text-sand-300 text-xs">Por apenas</span>
                <p className="text-3xl sm:text-4xl md:text-5xl font-bold text-white">
                  R$ 397<span className="text-lg sm:text-xl md:text-2xl">,00</span>
                </p>
                <span className="text-sand-400 text-xs">ou 12x de R$ 41,06</span>
              </div>
              <Button 
                href="https://hotmart.com/en/marketplace/products/beyond-class-beginner-t-1/W97266297T" 
                size="lg" 
                className="w-full bg-gradient-to-r from-olive-500 to-olive-600 hover:from-olive-400 hover:to-olive-500 text-white font-bold text-base sm:text-lg py-3 sm:py-4"
                icon="ri-shopping-cart-2-line"
              >
                Quero começar agora
              </Button>
              <p className="text-sand-500 text-[10px] sm:text-xs mt-3 sm:mt-4 flex items-center justify-center gap-1 sm:gap-2">
                <i className="ri-shield-check-line text-olive-400"></i>
                Garantia de 7 dias · Pagamento seguro pela Hotmart
              </p>
            </motion.div>
          </div>
        </div>

        {/* Indicador de scroll */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="text-sand-500"
          >
            <i className="ri-arrow-down-s-line text-2xl"></i>
          </motion.div>
        </motion.div>
      </section>

      {/* Sneak Peak - Preview do curso */}
      <Section bgColor="white">
        <div className="max-w-6xl mx-auto">
          <FadeInUp className="text-center mb-8 sm:mb-12">
            <span className="inline-flex items-center gap-2 bg-olive-100 text-olive-700 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4">
              <i className="ri-eye-line"></i>
              Sneak peek
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-earth-800 mb-3 sm:mb-4">
              Dá uma espiada no que<br />
              <span className="text-olive-600">você vai aprender</span>
            </h2>
          </FadeInUp>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 sm:gap-8">
            {/* Video Preview */}
            <div className="lg:col-span-3">
              <motion.div 
                className="relative aspect-video bg-earth-900 rounded-2xl overflow-hidden cursor-pointer group"
                whileHover={{ scale: 1.02 }}
                onClick={() => setShowVideoPreview(true)}
              >
                <img 
                  src={ONLINE_COURSES_VIDEO_PREVIEW.url} 
                  alt="Preview do curso"
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-60 transition-opacity"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-earth-900/80 to-transparent" />
                
                {/* Play button */}
                <motion.div 
                  className="absolute inset-0 flex items-center justify-center"
                  whileHover={{ scale: 1.1 }}
                >
                  <div className="w-20 h-20 bg-olive-500 rounded-full flex items-center justify-center shadow-2xl">
                    <i className="ri-play-fill text-4xl text-white ml-1"></i>
                  </div>
                </motion.div>

                {/* Info overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <span className="bg-olive-500 text-white text-xs px-2 py-1 rounded-full">Sneak Peek</span>
                  <h3 className="text-xl font-bold text-white mt-2">{ONLINE_COURSES_VIDEO_PREVIEW.title}</h3>
                  <p className="text-sand-300 text-sm">Beyond Class</p>
                </div>

              </motion.div>

              {/* Thumbnails de outras aulas */}
              <div className="grid grid-cols-4 gap-3 mt-4">
                {ONLINE_COURSES_THUMBNAILS.map((thumb, idx) => (
                  <motion.div
                    key={thumb.id}
                    className="relative aspect-video rounded-lg overflow-hidden cursor-pointer group"
                    whileHover={{ scale: 1.05 }}
                  >
                    <img src={thumb.url} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-earth-900/60 group-hover:bg-earth-900/40 transition-colors flex items-center justify-center">
                      <i className="ri-play-circle-line text-2xl text-white opacity-80"></i>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Módulos do curso */}
            <div className="lg:col-span-2">
              <h3 className="text-lg font-bold text-earth-800 mb-4 flex items-center gap-2">
                <i className="ri-book-open-line text-olive-600"></i>
                Conteúdo do curso
              </h3>
              <div className="space-y-3">
                {COURSE_MODULES.slice(0, showAllModules ? COURSE_MODULES.length : 5).map((module, idx) => (
                  <motion.button
                    key={module.number}
                    onClick={() => setActiveModule(idx)}
                    className={`w-full text-left p-4 rounded-xl transition-all ${
                      activeModule === idx 
                        ? 'bg-olive-50 border-2 border-olive-500' 
                        : 'bg-sand-50 border-2 border-transparent hover:border-sand-200'
                    }`}
                    whileHover={{ x: 4 }}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm ${
                        activeModule === idx ? 'bg-olive-500 text-white' : 'bg-sand-200 text-earth-600'
                      }`}>
                        {module.number}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-earth-800">{module.title}</h4>
                        <AnimatePresence>
                          {activeModule === idx && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="mt-2 pt-2 border-t border-olive-200"
                            >
                              <ul className="space-y-1">
                                {module.topics.map((topic) => (
                                  <li key={topic} className="text-xs text-olive-700 flex items-center gap-1">
                                    <i className="ri-check-line"></i>
                                    {topic}
                                  </li>
                                ))}
                              </ul>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  </motion.button>
                ))}
              </div>

              {/* Botão Ver Mais/Menos */}
              {!showAllModules && (
                <motion.button
                  onClick={() => setShowAllModules(true)}
                  className="w-full mt-3 p-3 bg-sand-50 hover:bg-sand-100 border-2 border-sand-200 rounded-xl text-earth-700 font-medium text-sm flex items-center justify-center gap-2 transition-colors"
                  whileHover={{ scale: 1.02 }}
                >
                  Ver mais {COURSE_MODULES.length - 5} aulas
                  <i className="ri-arrow-down-s-line"></i>
                </motion.button>
              )}

              {showAllModules && (
                <motion.button
                  onClick={() => setShowAllModules(false)}
                  className="w-full mt-3 p-3 bg-sand-50 hover:bg-sand-100 border-2 border-sand-200 rounded-xl text-earth-700 font-medium text-sm flex items-center justify-center gap-2 transition-colors"
                  whileHover={{ scale: 1.02 }}
                >
                  Ver menos
                  <i className="ri-arrow-up-s-line"></i>
                </motion.button>
              )}

              {/* Total do curso */}
              <div className="mt-4 p-4 bg-olive-600 rounded-xl text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-bold">10 aulas</p>
                    <p className="text-olive-200 text-sm">30 min cada</p>
                  </div>
                  <div className="text-right">
                    <p className="text-olive-200 text-sm">Acesso</p>
                    <p className="font-bold">por 6 meses</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Bônus - Aula ao vivo com Elienai */}
      <Section bgColor="sand">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Foto e info da professora */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative">
                {/* Foto placeholder - seria a foto real da Elienai */}
                <div className="aspect-[4/5] rounded-3xl overflow-hidden bg-gradient-to-br from-olive-200 to-sand-200">
                  <img 
                    src={TEACHER_PHOTOS[0].url} 
                    alt="Teacher Elienai"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                
                {/* Badge flutuante */}
                <motion.div 
                  className="absolute -bottom-4 -right-4 bg-white rounded-2xl shadow-xl p-4"
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3, type: 'spring' }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-olive-100 rounded-full flex items-center justify-center">
                      <i className="ri-live-line text-xl text-olive-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-earth-500">BÔNUS EXCLUSIVO</p>
                      <p className="font-bold text-earth-800">Aula ao vivo</p>
                    </div>
                  </div>
                </motion.div>

                {/* Decoração */}
                <div className="absolute -top-4 -left-4 w-24 h-24 bg-olive-500/20 rounded-full blur-2xl" />
              </div>
            </motion.div>

            {/* Conteúdo */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-flex items-center gap-2 bg-olive-100 text-olive-700 px-3 py-1 rounded-full text-sm font-medium mb-4">
                <i className="ri-gift-line"></i>
                Bônus incluso
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-earth-800 mb-4">
                2 Aulas ao vivo com a<br />
                <span className="text-olive-600">Teacher Elienai</span>
              </h2>
              <p className="text-earth-600 mb-6 leading-relaxed">
                Além das 10 videoaulas gravadas, você ganha 2 aulas ao vivo 
                de conversação em grupo (1h cada). A primeira após a 5ª aula 
                e a segunda ao finalizar o curso. Pratique o que aprendeu!.
              </p>

              {/* Benefícios da aula ao vivo */}
              <div className="space-y-4 mb-8">
                {[
                  { icon: 'ri-question-answer-line', title: 'Tire suas dúvidas', desc: 'Pergunte qualquer coisa sobre inglês' },
                  { icon: 'ri-user-voice-line', title: 'Pratique conversação', desc: 'Fale inglês em tempo real com correção' },
                  { icon: 'ri-calendar-check-line', title: 'Agende pelo app', desc: 'Escolha o melhor horário para você' },
                  { icon: 'ri-team-line', title: 'Turma pequena', desc: 'Grupos reduzidos para atenção total' },
                ].map((benefit) => (
                  <div key={benefit.title} className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-olive-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className={`${benefit.icon} text-olive-600`}></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-earth-800">{benefit.title}</h4>
                      <p className="text-earth-500 text-sm">{benefit.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Valor do bônus */}
              <div className="bg-white rounded-2xl p-6 border-2 border-dashed border-olive-300">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-earth-500 text-sm">2 aulas ao vivo (1h cada)</p>
                    <p className="text-2xl font-bold text-earth-800"><span className="line-through">R$ 500,00</span> <span className="text-olive-600 text-lg font-normal">INCLUSO</span></p>
                  </div>
                  <div className="w-14 h-14 bg-olive-100 rounded-full flex items-center justify-center">
                    <i className="ri-gift-2-line text-2xl text-olive-600"></i>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </Section>

      {/* O que você vai aprender */}
      <Section bgColor="white">
        <FadeInUp className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-earth-800 mb-4">
            O que você pode conquistar<br />
            <span className="text-olive-600">após o curso</span>
          </h2>
          <p className="text-earth-600 max-w-2xl mx-auto">
            Resultados reais que você vai ter depois de completar o curso
          </p>
        </FadeInUp>

        <StaggerContainer className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
          {[
            { icon: 'ri-plane-line', skill: 'Viajar sem medo', desc: 'Se vire em aeroportos e hotéis' },
            { icon: 'ri-movie-2-line', skill: 'Assistir sem legenda', desc: 'Filmes e séries em inglês' },
            { icon: 'ri-chat-3-line', skill: 'Conversar de verdade', desc: 'Com nativos e estrangeiros' },
            { icon: 'ri-briefcase-line', skill: 'Entrevistas em inglês', desc: 'Oportunidades de trabalho' },
            { icon: 'ri-music-2-line', skill: 'Entender músicas', desc: 'Cantar junto sabendo o que diz' },
            { icon: 'ri-smartphone-line', skill: 'Redes sociais', desc: 'Consumir conteúdo em inglês' },
            { icon: 'ri-user-heart-line', skill: 'Fazer amigos', desc: 'De qualquer lugar do mundo' },
            { icon: 'ri-brain-line', skill: 'Inglês associativo', desc: 'Sem traduzir na cabeça' },
          ].map((item) => (
            <StaggerItem key={item.skill}>
              <Card className="text-center h-full">
                <div className="w-12 h-12 bg-olive-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <i className={`${item.icon} text-xl text-olive-600`}></i>
                </div>
                <h3 className="font-semibold text-earth-800 mb-1">{item.skill}</h3>
                <p className="text-earth-500 text-sm">{item.desc}</p>
              </Card>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </Section>

      {/* Depoimentos em Vídeo */}
      <section className="section-padding bg-olive-700">
        <div className="container-custom">
        <FadeInUp className="text-center mb-12">
          <div className="max-w-2xl mx-auto">
            <span className="inline-flex items-center gap-2 bg-olive-100 text-olive-700 px-3 py-1 rounded-full text-sm font-medium mb-4">
              <i className="ri-video-line"></i>
              Depoimentos reais
            </span>

            <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
              Quem já fez, recomenda
            </h2>

            <p className="text-sand-200 mb-4">
              Veja o que nossos alunos têm a dizer sobre o curso.
            </p>
          </div>
        </FadeInUp>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {VIDEO_TESTIMONIALS.map((testimonial, idx) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="bg-white rounded-2xl overflow-hidden shadow-lg cursor-pointer group"
              onClick={() => setActiveTestimonial(testimonial.youtubeId)}
            >
              {/* Video thumbnail */}
              <div className="relative aspect-[9/16]">
                <img 
                  src={`https://img.youtube.com/vi/${testimonial.youtubeId}/maxresdefault.jpg`}
                  alt={`Depoimento de ${testimonial.author}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Fallback para thumbnail de qualidade média se maxres não existir
                    e.currentTarget.src = `https://img.youtube.com/vi/${testimonial.youtubeId}/hqdefault.jpg`;
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-earth-900/80 to-transparent" />
                
                {/* Play button overlay */}
                <motion.div 
                  className="absolute inset-0 flex items-center justify-center"
                  whileHover={{ scale: 1.1 }}
                >
                  <div className="w-16 h-16 bg-olive-500 rounded-full flex items-center justify-center shadow-2xl group-hover:bg-olive-400 transition-colors">
                    <i className="ri-play-fill text-3xl text-white ml-1"></i>
                  </div>
                </motion.div>

                {/* Info overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <span className="bg-olive-500 text-white text-xs px-2 py-1 rounded-full">Depoimento</span>
                </div>
              </div>
              
              {/* Author info */}
              <div className="p-4">
                <p className="font-semibold text-earth-800">{testimonial.author}</p>
                <p className="text-earth-500 text-sm flex items-center gap-1">
                  <i className="ri-map-pin-line text-olive-600 text-xs"></i>
                  {testimonial.location}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
        </div>
      </section>

      {/* Garantia e FAQ */}
      <Section bgColor="sand">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Garantia */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl p-8 text-center"
            >
              <div className="w-20 h-20 bg-olive-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-4xl text-olive-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-earth-800 mb-4">
                Garantia de 7 dias
              </h3>
              <p className="text-earth-600 mb-6">
                Se em 7 dias você sentir que o curso não é pra você, 
                devolvemos 100% do seu dinheiro. Sem perguntas.
              </p>
              <div className="flex items-center justify-center gap-4 text-sm text-earth-500">
                <span className="flex items-center gap-1">
                  <i className="ri-check-line text-olive-600"></i>
                  Sem burocracia
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-check-line text-olive-600"></i>
                  Reembolso total
                </span>
              </div>
            </motion.div>

            {/* FAQ rápido */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-4"
            >
              <h3 className="text-xl font-bold text-earth-800 mb-4">Dúvidas frequentes</h3>
              {[
                { q: 'Por quanto tempo tenho acesso?', a: 'Acesso por 6 meses. Aproveite ao máximo!' },
                { q: 'Quanto tempo duram as aulas?', a: 'Cada videoaula tem de 20 a 30 minutos, com exercícios de fixação.' },
                { q: 'Como funcionam as aulas ao vivo?', a: 'São 2 aulas de conversação em grupo (1h cada). A 1ª após a 5ª aula e a 2ª ao finalizar. Agende pelo link na plataforma.' },
                { q: 'O que está incluso no curso?', a: '10 videoaulas + 2 aulas ao vivo + plano de estudos em PDF + recursos extras.' },
              ].map((faq) => (
                <details key={faq.q} className="group bg-white rounded-xl overflow-hidden">
                  <summary className="flex items-center justify-between p-4 cursor-pointer list-none">
                    <span className="font-medium text-earth-800">{faq.q}</span>
                    <i className="ri-add-line text-olive-600 group-open:rotate-45 transition-transform"></i>
                  </summary>
                  <div className="px-4 pb-4 text-earth-600 text-sm">
                    {faq.a}
                  </div>
                </details>
              ))}
            </motion.div>
          </div>
        </div>
      </Section>

      {/* CTA Final */}
      <Section bgColor="dark">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Comece a falar inglês<br />
              <span className="text-olive-400">ainda hoje</span>
            </h2>
            
            {/* Resumo do que leva */}
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              {[
                '10 videoaulas',
                '2 aulas ao vivo',
                'Plano de estudos PDF',
                'Acesso por 6 meses',
                'Garantia 7 dias',
              ].map((item) => (
                <span key={item} className="flex items-center gap-2 text-sand-300 text-sm">
                  <i className="ri-check-double-line text-olive-400"></i>
                  {item}
                </span>
              ))}
            </div>

            {/* Preço final */}
            <div className="bg-gradient-to-br from-olive-600 to-olive-700 rounded-3xl p-8 mb-8">
              <p className="text-6xl font-bold text-white mb-2">
                R$ 397<span className="text-3xl">,00</span>
              </p>
              <p className="text-olive-200 mb-6">ou 12x de R$ 41,06</p>
              
              <a 
                href="https://hotmart.com/en/marketplace/products/beyond-class-beginner-t-1/W97266297T" 
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-sand-100 text-earth-800 font-bold text-lg px-12 py-4 rounded-lg transition-all duration-200"
              >
                Quero aprender inglês
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            <p className="text-sand-500 text-sm flex items-center justify-center gap-2">
              <i className="ri-lock-line"></i>
              Compra 100% segura · Ambiente protegido Hotmart
            </p>
          </motion.div>
        </div>
      </Section>

      {/* Video Preview Modal */}
      <AnimatePresence>
        {showVideoPreview && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-earth-900/95 z-50 flex items-center justify-center p-4"
            onClick={() => setShowVideoPreview(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative w-full max-w-4xl aspect-video bg-earth-900 rounded-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <iframe
                src="https://www.youtube.com/embed/V03UqtswPXQ?autoplay=1"
                title="Aula de demonstração"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </motion.div>
            
            <button 
              onClick={() => setShowVideoPreview(false)}
              className="absolute top-4 right-4 w-12 h-12 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
            >
              <i className="ri-close-line text-2xl text-white"></i>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Testimonial Video Modal */}
      <AnimatePresence>
        {activeTestimonial && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-earth-900/95 z-50 flex items-center justify-center p-4"
            onClick={() => setActiveTestimonial(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative w-full max-w-4xl aspect-video bg-earth-900 rounded-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <iframe
                src={`https://www.youtube.com/embed/${activeTestimonial}?autoplay=1&rel=0&modestbranding=1`}
                title="Depoimento"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </motion.div>
            
            <button 
              onClick={() => setActiveTestimonial(null)}
              className="absolute top-4 right-4 w-12 h-12 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
            >
              <i className="ri-close-line text-2xl text-white"></i>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
